import { Component, computed, effect, inject, input, OnDestroy, OnInit, output, signal, viewChild } from '@angular/core';
import { delay, shuffle } from '@gametailors/web-core/misc';
import { RidSfxDirective } from '../../../directives/rid-sfx.directive';
import { IconEnter } from '../../../icons/i-enter';
import { PronunciationService } from '../../../services/pronunciation.service';
import { AudioEffectPlayer } from '../../../utils/audio.utils';
import { DebouncedCallback } from '../../../utils/debounced-callback.utils';
import { RidBottomButton } from '../../rid-bottom-button/rid-bottom-button';
import { RidButton } from '../../rid-button/rid-button';
import { RidDraggable } from '../../rid-draggable/rid-draggable';
import { RidKlankCard } from '../../rid-klank-card/rid-klank-card';
import { RidLettermixWord } from '../../rid-lettermix-word/rid-lettermix-word';
import { RidMechanicBackground } from '../../rid-mechanic-background/rid-mechanic-background';
import { RidMechanicContent } from '../../rid-mechanic-content/rid-mechanic-content';
import { RidMechanicTitle } from '../../rid-mechanic-title/rid-mechanic-title';
import { RidMechanicWrapper } from '../../rid-mechanic-wrapper/rid-mechanic-wrapper';
import { RidOverlayFeedback } from '../../rid-overlay-feedback/rid-overlay-feedback';
import { RidSpacer } from '../../rid-spacer/rid-spacer';
import { RidTopBar } from '../../rid-top-bar/rid-top-bar';
import { RidWordBuildingKern } from './rid-word-building-kern';
import { RidWordBuildingSlot } from './rid-word-building-slot';

type WordTuple = [prefix: string | undefined, root: string, suffix: string | undefined];

@Component({
  selector: 'rid-word-building',
  imports: [
    RidMechanicBackground,
    RidMechanicWrapper,
    RidTopBar,
    RidMechanicContent,
    RidMechanicTitle,
    RidOverlayFeedback,
    RidButton,
    RidLettermixWord,
    RidBottomButton,
    RidKlankCard,
    RidDraggable,
    RidSpacer,
    RidWordBuildingSlot,
    RidWordBuildingKern,
    IconEnter,
    RidSfxDirective,
  ],
  templateUrl: './rid-word-building.html',
  host: { class: 'block' },
})
export class RidWordBuilding implements OnInit, OnDestroy {
  private readonly $pronunciationService = inject(PronunciationService);

  assignments = input.required<{ root: string; words: WordTuple[] }[]>();

  index = signal(0);
  progress = signal(0);
  prefix = signal<string | undefined>(undefined);
  suffix = signal<string | undefined>(undefined);
  isChecking = signal(false);
  // The kern of the word being checked when a morpheme changes it (loop + en -> lop-en), played in place by the kern card.
  changedKern = signal<string | undefined>(undefined);
  kernPhase = signal<'ROOT' | 'CHANGED'>('ROOT');
  assignment = signal<{
    id: string;
    options: string[];
    root: string;
    answers: { word: WordTuple; display: string[]; appearance: 'REVEAL' | 'CORRECT' | 'SPOILER' }[];
  }>({ id: crypto.randomUUID(), options: [], root: '', answers: [] });

  canGoToNext = computed(() => this.assignment().answers.every((a) => a.appearance !== 'SPOILER'));
  hasInput = computed(() => this.prefix() !== undefined || this.suffix() !== undefined);
  longestOption = computed(() => {
    const options = this.assignment().options;
    return options.reduce((longest, o) => (o.length > longest.length ? o : longest), '');
  });
  currentWord = computed(() => [this.prefix(), this.assignment().root, this.suffix()].filter((p): p is string => p !== undefined).join(''));
  seenWords = computed(() =>
    this.assignment()
      .answers.filter((a) => a.appearance === 'CORRECT')
      .map((a) => a.display.join('')),
  );
  overlayFeedback = viewChild.required<RidOverlayFeedback>('overlayFeedback');
  revealStage = signal<'OPTIONS' | 'ANSWERS' | 'INPUT'>('OPTIONS');
  prefixDropZone = viewChild.required<RidWordBuildingSlot>('prefixDropZone');
  suffixDropZone = viewChild.required<RidWordBuildingSlot>('suffixDropZone');
  canRevealAnswers = signal(false);
  private readonly revealAnswersHint = new DebouncedCallback(() => this.canRevealAnswers.set(true), 5000);

  finished = output<void>();

  constructor() {
    effect(() => {
      if (this.canRevealAnswers()) {
        AudioEffectPlayer.play('HINT');
      }
    });
  }

  ngOnInit(): void {
    this.initialize(this.assignments()[this.index()]);
  }

  ngOnDestroy(): void {
    this.revealAnswersHint.cancel();
  }

  initialize(data: { root: string; words: WordTuple[] }) {
    const allParts = data.words.flatMap((w) => [w[0], w[2]]).filter((p): p is string => p !== undefined);
    const options = shuffle(Array.from(new Set(allParts)));

    this.assignment.set({
      id: crypto.randomUUID(),
      options,
      root: data.root,
      answers: data.words.map((w) => ({
        word: w,
        display: [w[0], w[1], w[2]].filter((p): p is string => p !== undefined),
        appearance: 'SPOILER' as const,
      })),
    });

    this.revealStage.set('OPTIONS');
    this.canRevealAnswers.set(false);
    this.revealAnswersHint.activate();
  }

  revealAnswers() {
    this.revealStage.set('ANSWERS');
  }

  revealInput() {
    this.revealStage.set('INPUT');
  }

  onDragMove(draggable: RidDraggable, point: { x: number; y: number }) {
    if (this.isChecking()) return;

    for (const zone of [this.prefixDropZone(), this.suffixDropZone()]) {
      zone.handleMove(draggable, point);
    }
  }

  onDragRelease(draggable: RidDraggable, optionIndex: number) {
    const klank = this.assignment().options[optionIndex];
    for (const [zone, slot] of [
      [this.prefixDropZone(), 'PREFIX'],
      [this.suffixDropZone(), 'SUFFIX'],
    ] as const) {
      if (zone.isSnapped(draggable)) {
        zone.unsnap(draggable);

        if (slot === 'PREFIX') this.prefix.set(klank);
        else this.suffix.set(klank);

        this.revealAnswersHint.activate();
        draggable.respawn();
        return;
      }
    }

    this.revealAnswersHint.activate();
  }

  clearSlot(slot: 'PREFIX' | 'SUFFIX') {
    if (this.isChecking()) return;

    if (slot === 'PREFIX') this.prefix.set(undefined);
    else this.suffix.set(undefined);

    this.revealAnswersHint.activate();
  }

  async check() {
    if (this.isChecking()) return;

    const prefix = this.prefix() ?? undefined;
    const suffix = this.suffix() ?? undefined;
    const words = this.assignments()[this.index()].words;
    const answers = this.assignment().answers;

    const correctWordIndex = words.findIndex((w, i) => w[0] === prefix && w[2] === suffix && answers[i].appearance === 'SPOILER');

    if (correctWordIndex >= 0) {
      const word = this.currentWord();
      const root = this.assignment().root;
      const kern = words[correctWordIndex][1];

      if (kern !== root) {
        // Keep the morphemes in their slots while the kern changes in place, so the child sees why: loop + en -> lop-en.
        this.isChecking.set(true);
        this.revealAnswersHint.cancel();

        // The kern is set a moment before the phase, so added letters exist (collapsed) before they expand.
        this.changedKern.set(kern);
        await delay(50);
        this.kernPhase.set('CHANGED');
        await delay(1100);

        AudioEffectPlayer.play('CORRECT');
        this.overlayFeedback().showCorrect();
        await delay(250);
        await this.$pronunciationService.tryPronounce(word);
        await delay(400);

        this.markCorrect(correctWordIndex);
        this.kernPhase.set('ROOT');
        this.isChecking.set(false);
      } else {
        this.markCorrect(correctWordIndex);
        AudioEffectPlayer.play('CORRECT');
        this.overlayFeedback().showCorrect();
        await delay(250);
        await this.$pronunciationService.tryPronounce(word);
      }
    } else {
      this.prefix.set(undefined);
      this.suffix.set(undefined);
      AudioEffectPlayer.play('INCORRECT');
      this.overlayFeedback().showIncorrect();
    }

    this.revealAnswersHint.activate();
  }

  private markCorrect(wordIndex: number) {
    this.assignment.update((assignment) => {
      const newAssignment = { ...assignment };
      newAssignment.answers[wordIndex].appearance = 'CORRECT';
      return newAssignment;
    });
    this.prefix.set(undefined);
    this.suffix.set(undefined);
  }

  next() {
    const nextIndex = this.index() + 1;
    if (nextIndex >= this.assignments().length) {
      this.finish();
    } else {
      this.index.set(nextIndex);
      this.prefix.set(undefined);
      this.suffix.set(undefined);
      this.initialize(this.assignments()[nextIndex]);
      this.progress.update((p) => p + 1);
    }
  }

  reveal() {
    this.prefix.set(undefined);
    this.suffix.set(undefined);
    this.assignment.update((assignment) => {
      const newAssignment = { ...assignment };
      newAssignment.answers = newAssignment.answers.map((a) => ({
        ...a,
        appearance: a.appearance === 'SPOILER' ? ('REVEAL' as const) : a.appearance,
      }));
      return newAssignment;
    });

    this.revealAnswersHint.cancel();
  }

  finish() {
    this.revealAnswersHint.cancel();
    this.finished.emit();
  }
}
